import { Fragment, useMemo } from "react";

import { useChat } from "@/features/chat";

import ChartReportsDecoration from "@/assets/img/today/reports/chart_reports_decoration.webp";
import Chart2Color from "@/assets/img/today/reports/chart/chart_2.webp";
import Chart3Color from "@/assets/img/today/reports/chart/chart_3.webp";
import Chart4Color from "@/assets/img/today/reports/chart/chart_4.webp";
import Chart5Color from "@/assets/img/today/reports/chart/chart_5.webp";
import Chart6Color from "@/assets/img/today/reports/chart/chart_6.webp";
import Chart7Color from "@/assets/img/today/reports/chart/chart_7.webp";
import PurchasedIcon from "@/assets/img/today/reports/purchased_icon.svg?react";
import StarFull from "@/assets/img/today/reports/star_full.webp";
import Star47 from "@/assets/img/today/reports/star_4.7.webp";
import Star48 from "@/assets/img/today/reports/star_4.8.webp";
import Star49 from "@/assets/img/today/reports/star_4.9.webp";

import { REPORTS, type ReportItem } from "../../config/reports-data";
import { useHorizontalWheelScroll } from "../../hooks/use-horizontal-wheel-scroll";
import { useReportCta } from "../../hooks/use-report-cta";
import { useReportPurchaseStore } from "../../hooks/report-purchase-store";
import { useReportStates } from "../../hooks/use-report-states";
import { useReportUnlock } from "../../hooks/use-report-unlock";
import { useTabReportTimerLabel } from "../../hooks/use-tab-report-timer-label";
import { useUserReports } from "../../hooks/use-user-reports";
import { getPurchasedReportLabel } from "../../lib/report-actions";
import { ReportGetButton } from "./report-get-button";

const CHART_BY_PURCHASED_COLOR: Record<number, string> = {
  3: Chart3Color,
  4: Chart4Color,
  5: Chart5Color,
  6: Chart6Color,
  7: Chart7Color,
};

const CHART_BY_PURCHASED_COLOR_OUT_OF_4: Record<number, string> = {
  3: Chart5Color,
  4: Chart6Color,
};

const lastStarIcon = (rating: number) =>
  rating === 4.7 ? Star47 : rating === 4.8 ? Star48 : Star49;

/** Personalized reports catalog. Ported from Today/Reports. */
export function ReportsCategory() {
  const { userMainSubscriptionId } = useChat();
  const reportsContainerRef = useHorizontalWheelScroll<HTMLDivElement>();
  const { handleReportCta, isReportPurchasing } = useReportCta({ userMainSubscriptionId });

  const { reports: apiReports } = useUserReports();
  const stateOf = useReportStates();

  const reports = useMemo(
    () => REPORTS.map((report) => ({ report, ...stateOf(report) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiReports, stateOf],
  );

  const purchasedCount = reports.filter((item) => item.purchased).length;

  const chartColor =
    reports.length === 4
      ? (CHART_BY_PURCHASED_COLOR_OUT_OF_4[purchasedCount] ?? Chart3Color)
      : (CHART_BY_PURCHASED_COLOR[purchasedCount] ?? Chart2Color);

  return (
    <div className="w-full">
      <div className="select-none px-4">
        <div className="flex w-full justify-center items-center relative">
          <img
            src={chartColor}
            style={{
              width: "75%",
              bottom: 0,
              position: "absolute",
            }}
          />
          <img src={ChartReportsDecoration} alt="chart" />

          <div className="flex flex-col items-center justify-center absolute bottom-0">
            <h2 className="mt-4 mb-0 text-center font-display text-[40px] leading-none font-medium text-white drop-shadow-[0_2px_12px_rgba(149,101,253,0.56)] tracking-tight">
              <span className="text-purple-light">{purchasedCount}</span>
              <span className="mx-1 text-purple-light">/</span>
              <span className="text-purple-light">{reports.length}</span>
            </h2>
            <p className="mt-1 mb-1 text-center font-body text-[15px] font-medium text-purple-light/70 tracking-tight">
              Reports unlocked
            </p>
          </div>
        </div>
        <h2 className="mt-4 mb-3 text-center font-display text-2xl font-medium text-purple-light">
          Unlock more reports
          <br />
          to complete your reading
        </h2>

        <div className="flex w-full items-center justify-center gap-0.5 px-2">
          {reports.map(({ report, purchased }, index) => (
            <Fragment key={report.title}>
              {purchased ? (
                <PurchasedIcon className="size-7 min-w-7" />
              ) : (
                <span className="flex size-7 min-w-7 items-center justify-center rounded-full bg-purple-light/10 text-center font-body text-[11px] font-medium text-purple-light">
                  {index + 1}
                </span>
              )}
              {index < reports.length - 1 && (
                <span className="h-px w-full bg-purple-light/20" />
              )}
            </Fragment>
          ))}
        </div>

        <p className="mt-2 mb-6 text-center font-body text-base text-purple-light/70">
          Each new report adds missing details and makes your reading feel more
          personal
        </p>
      </div>

      <div
        ref={reportsContainerRef}
        className="flex w-full gap-4 overflow-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {reports.map(({ report, purchased, price }) => (
          <ReportCard
            key={report.title}
            report={report}
            purchased={purchased}
            price={price}
            isPurchasing={isReportPurchasing(report)}
            onCta={() => handleReportCta(report, purchased)}
          />
        ))}
      </div>
    </div>
  );
}

function ReportCard({
  report,
  purchased,
  price,
  isPurchasing,
  onCta,
}: {
  report: ReportItem;
  purchased: boolean;
  price: number;
  isPurchasing: boolean;
  onCta: () => void;
}) {
  // While the report is still generating, the card shows a spinner instead of
  // "View" and a tap re-opens the "Report in Progress" pop-up with the countdown
  // (same source as the cross-sell rows), rather than opening an unready report.
  const purchaseUnlock = useReportUnlock(report.id);
  const tabTimerLabel = useTabReportTimerLabel(report.id);
  const inProgress = purchaseUnlock.inProgress || tabTimerLabel !== null;
  const remainingLabel = tabTimerLabel ?? purchaseUnlock.remainingLabel;
  const showSheet = useReportPurchaseStore((s) => s.showSheet);

  return (
    <div className="min-w-80">
      <button
        type="button"
        onClick={() => {
          if (inProgress) {
            showSheet(report);
            return;
          }
          onCta();
        }}
        disabled={isPurchasing}
        // Back layer: while processing, the button IS the purple block (same radius)
        // sitting behind the card — it shows through at the card's rounded bottom
        // corners and pokes out below as the "unlocks in" bar. Otherwise it's just
        // the normal grey card. Literal strings (not cn) keep the two-class surface.
        className={
          inProgress
            ? "relative flex w-full flex-col overflow-hidden rounded-[20px] bg-main-purple text-left"
            : "relative flex w-full flex-col overflow-hidden rounded-[20px] bg-purple-light/10 bg-dark-base text-left"
        }
      >
        {/* Front layer: the report card. Solid dark-base + a purple-light/10 wash
            reproduces the exact grey of the other cards, but opaque — so the purple
            back layer only shows at the rounded bottom corners, not through the card. */}
        <div
          className={
            inProgress
              ? "relative flex w-full flex-col rounded-b-[20px] bg-dark-base"
              : "relative flex w-full flex-col"
          }
        >
          {inProgress && (
            <span className="pointer-events-none absolute inset-0 rounded-b-[20px] bg-purple-light/10" />
          )}

          {!purchased && (
            <span className="absolute top-0 left-0 z-10 rounded-br-[20px] bg-purple-light/10 px-4 py-1.5 font-body text-xs font-semibold text-white">
              ${price}
            </span>
          )}

          <div className="relative flex w-full px-5 pt-7">
            <img
              src={report.hugeCover}
              alt={report.title}
              className="h-full w-full object-cover"
            />
          </div>

          <div className="relative flex w-full items-center justify-between gap-2 p-4">
            <div className="flex flex-col justify-center gap-0.5">
              <h3 className="font-display text-base font-medium text-white">
                {report.title}
              </h3>
              <div className="flex items-center gap-1">
                <span className="flex items-center [&_img]:size-4">
                  <img src={StarFull} alt="" />
                  <img src={StarFull} alt="" />
                  <img src={StarFull} alt="" />
                  <img src={StarFull} alt="" />
                  <img src={lastStarIcon(report.rating)} alt="" />
                </span>
                <span className="font-body text-sm font-medium text-gold">
                  {report.rating}
                </span>
                <span className="font-body text-sm text-purple-light/70">
                  ({report.purchaseCount})
                </span>
              </div>
            </div>

            {inProgress ? (
              <span className="flex size-9 min-w-9 items-center justify-center rounded-full bg-purple-light/10">
                <span className="size-5 animate-spin rounded-full border-2 border-purple-light/20 border-t-purple-light" />
              </span>
            ) : purchased ? (
              <span className="flex h-9 items-center justify-center rounded-full bg-purple-light/10 px-6 font-body text-base font-semibold text-purple-light">
                {getPurchasedReportLabel()}
              </span>
            ) : (
              <ReportGetButton isLoading={isPurchasing} />
            )}
          </div>
        </div>

        {/* Poking out below the card: the back layer's "unlocks in" bar. */}
        {inProgress && (
          <div className="flex w-full items-center justify-center gap-1 py-2">
            <span className="font-body text-sm font-semibold text-white">
              Your reading unlocks in
            </span>
            <span className="flex h-5 items-center justify-center rounded-[10px] bg-white px-2 font-body text-xs font-bold tabular-nums text-dark-base">
              {remainingLabel}
            </span>
          </div>
        )}
      </button>
    </div>
  );
}
